import { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import Footer from "../../includes/footer-tab"; // Adjust path
import Preloader from "../Preloader"; // Adjust path
import BackButton from "../../includes/Back_btn"; // Adjust path
import Table from "../../includes/Table"; // Adjust path
import MarketTable from "../../includes/MarketTable"; // Adjust path
import { useParams, useNavigate, Link } from "react-router-dom"; // Added Link
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { AuthContext } from "../../context/AuthContext"; // Adjust path
import { ToastContainer, toast } from "react-toastify"; // Added Toast
import 'react-toastify/dist/ReactToastify.css';
import styles from '../../assets/css/Transaction.module.css'; // Import NEW CSS Module

function Transaction() {
  const { id } = useParams(); // History type (e.g., 'market', 'recharge', 'withdrawal', 'win')
  const [preloader, setPreloader] = useState(true);
  const [data, setData] = useState([]); // Changed state variable name
  const [pagenumber, setPagenumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // To disable next button if on last page
  const [totalItems, setTotalItems] = useState(0); // For display "1-10 of X"
  const [itemsPerPage, setItemsPerPage] = useState(10); // Assuming 10 items per page

  const { isAuthenticated, Transactiondata, CancelBet } = useContext(AuthContext);
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Function to capitalize the first letter and handle specific cases
  function capitalize(str) {
    if (typeof str !== "string" || str.length === 0) return "History";
    if (str === 'marketcancel') return 'Cancelled Bets';
    if (str === 'market') return 'Bid History';
    if (str === 'win') return 'Win History';
    // Add more cases as needed (e.g., 'recharge', 'withdrawal')
    return str.charAt(0).toUpperCase() + str.slice(1) + ' History'; // Add " History" suffix
  }

  // Toast message function
  const message = (status, text) => {
    const options = {
        position: "top-right", autoClose: 3000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true,
        progress: undefined, theme: "dark", // Use dark theme
    };
    if (status === 1) toast.success(text, options);
    else if (status === 0) toast.error(text, options);
    else if (status === 2) toast.warning(text, options);
  };


  // Fetch data function
  const fetchData = (page) => {
    setPreloader(true);
    setData([]); // Clear previous data while loading new page
    if (Transactiondata) {
      Transactiondata(id, page, (res) => {
        setPreloader(false);
        if (res.status === 0) {
          message(0, res.msg || `No more ${capitalize(id)} found.`);
          // If page > 1 and no data, likely went past the last page
          if (page > 1) {
            setPagenumber(page - 1); // Stay on the previous valid page number
          }
          setData([]); // Ensure data is empty
          setTotalPages(pagenumber); // Assume current page is last page
          setTotalItems( (pagenumber -1) * itemsPerPage) // Estimate total items
        } else if (res.data && Array.isArray(res.data)) {
          setData(res.data);
          setPagenumber(page); // Update page number
          // Assuming API might return total count or infer from results
          const receivedCount = res.data.length;
          setTotalItems(res.total_records || ((page - 1) * itemsPerPage + receivedCount)); // Use total_records if available
          setTotalPages(Math.ceil((res.total_records || ((page - 1) * itemsPerPage + receivedCount)) / itemsPerPage ));

          // If received less data than itemsPerPage, assume it's the last page
          if (receivedCount < itemsPerPage) {
              setTotalPages(page);
          }

        } else {
           message(2, `No ${capitalize(id)} found for this page.`);
           if (page > 1) {
             setPagenumber(page - 1); // Go back
           }
           setData([]);
           setTotalPages(pagenumber);
           setTotalItems( (pagenumber -1) * itemsPerPage)
        }
      });
    } else {
        setPreloader(false);
        message(0, "Transaction data function not available.");
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData(1); // Fetch page 1 initially
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Refetch if the history type 'id' changes

  // Pagination handlers
  const handleBack = () => {
    if (pagenumber > 1) {
      fetchData(pagenumber - 1);
    }
    // No message needed, button will be disabled
  };

  const handleNext = () => {
    if (pagenumber < totalPages) {
        fetchData(pagenumber + 1);
    }
     // No message needed, button will be disabled
  };

  // Cancel Bet handler
  const handleDeleteBet = (gameId) => {
    if (!gameId) { message(0, "Invalid bet ID."); return; }
    setPreloader(true);
    if (CancelBet) {
        CancelBet(gameId, (res) => {
            setPreloader(false);
            if (res.status === 1) {
                message(1, res.msg || "Bet cancelled successfully!");
                fetchData(pagenumber); // Refetch current page data
            } else {
                message(0, res.msg || "Failed to cancel bet.");
            }
        });
    } else {
         setPreloader(false);
         message(0, "Cancel bet function not available.");
    }
  };

  // Framer Motion variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };
  const itemVariants = {
    hidden: { y: 15, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'tween', duration: 0.3 } },
  };

  // Determine which table component to use
  const isMarketHistory = id === "market" || id === "marketcancel";
  const startItem = (pagenumber - 1) * itemsPerPage + 1;
  const endItem = startItem + data.length - 1;


  return (
    <>
      <Preloader status={preloader} />
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Use the Aghori theme container */}
      <div className={styles.aghoriTransactionContainer}>
          <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
          >
              {/* --- Header --- */}
              <motion.div className={styles.aghoriHeader} variants={itemVariants}>
                  <BackButton className={styles.backButton} />
                  <p className={styles.headerTitle}>{capitalize(id)}</p>
                  <div className={styles.headerPlaceholder}></div>
              </motion.div>

              {/* --- History List --- */}
              <motion.div className={styles.historyList} variants={itemVariants}>
                  {preloader && data.length === 0 ? (
                      // Optional: Show a simple loading text while fetching pages
                      <p className={styles.loadingMessage}>Loading data...</p>
                  ) : data.length > 0 ? (
                      isMarketHistory ? (
                          data.map((element) => (
                              <MarketTable
                                  key={element.id || element.game_id} // Use a unique key
                                  data={element}
                                  purpose={id}
                                  Oncancelfun={handleDeleteBet}
                              />
                          ))
                      ) : (
                          // Render generic Table component
                          data.map((element, index) => (
                              <Table
                                  key={element.id || index} // Use element.id if available
                                  data={element}
                                  purpose={id}
                              />
                          ))
                      )
                  ) : (
                      // Show message when no data is available
                      <p className={styles.noDataMessage}>No {capitalize(id)} found.</p>
                  )}
              </motion.div>

              {/* --- Pagination --- */}
              {/* Only show pagination if there's data */}
              {data.length > 0 && (
                  <motion.div className={styles.pagination} variants={itemVariants}>
                      <ul className={styles.pageBox}>
                          <li className={styles.pageInfo}>
                              {/* Display item range */}
                              <span>{startItem}-{endItem}</span> of {totalItems}
                          </li>
                          <li className={styles.pageBtnContainer}>
                              <button onClick={handleBack} disabled={pagenumber <= 1 || preloader} className={styles.pageButton}>
                                  <IoIosArrowBack />
                              </button>
                              <button onClick={handleNext} disabled={pagenumber >= totalPages || preloader} className={styles.pageButton}>
                                  <IoIosArrowForward />
                              </button>
                          </li>
                      </ul>
                  </motion.div>
              )}
          </motion.div>
      </div>
      {/* --- Footer --- */}
      <Footer /> {/* Ensure Footer component uses the correct CSS */}
    </>
  );
}

export default Transaction;
